import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import UploadIcon from '@mui/icons-material/Upload'
import BarChartIcon from '@mui/icons-material/BarChart'
import Header from '../Components/layout/Header'
import Graph from '../Components/ui/graph'
import { Context } from '../App'
import { API } from '../conf'

export default function Fournisseurs() {
    const { exercice, updateConnect } = useContext(Context)
    const [organisation] = useState(JSON.parse(sessionStorage.getItem('eglise')))
    const [agent] = useState(JSON.parse(sessionStorage.getItem('user')))
    const [eglises, setEglises] = useState([])
    const [egliseId, setEgliseId] = useState('')
    const [fournisseurs, setFournisseurs] = useState([])
    const [loading, setLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [graphOpen, setGraphOpen] = useState(false)
    const [current, setCurrent] = useState(null)
    const [libelle, setLibelle] = useState('')
    const [numCompte, setNumCompte] = useState('')
    const csvRef = useRef()

    const eglise = useMemo(
        () => organisation?.type === 'église' ? organisation : eglises.find(el => el._id === egliseId),
        [egliseId, eglises, organisation]
    )

    async function doFetch(url, options = {}) {
        const response = await fetch(API.concat(url), {
            ...options,
            headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('token'), 'Content-Type': 'application/json', ...options.headers }
        })
        const token = response.headers.get('x-auth-token')
        if (response.status === 401) { sessionStorage.clear(); updateConnect(false) }
        else if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token)
        return response
    }

    function fetchFournisseurs() {
        setLoading(true)
        const query = new URLSearchParams({ eglise: eglise.number, exercice, type: 'fournisseur' }).toString()
        doFetch(`/comptes/by?${query}`)
            .then(r => r.json())
            .then(data => { setFournisseurs(data); setLoading(false) })
            .catch(() => setLoading(false))
    }

    useEffect(() => { if (eglise && exercice) fetchFournisseurs() }, [eglise, exercice])

    useEffect(() => {
        if (organisation?.type === 'fédération') {
            const query = agent?.eglises?.length > 0
                ? { number: { $in: agent.eglises } }
                : agent?.level >= 4 ? { type: { $in: ['église', 'fédération'] } } : { type: 'église' }
            fetch(API.concat(`/eglises/by/${JSON.stringify(query)}`), {
                method: 'GET',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + sessionStorage.getItem('token') }
            }).then(async r => { if (r.status === 200) setEglises(await r.json()) })
        }
    }, [organisation, agent])

    function openCreate() { setCurrent(null); setLibelle(''); setNumCompte(''); setDialogOpen(true) }
    function openEdit(f) { setCurrent(f); setLibelle(f.libelle); setNumCompte(f.num_compte || ''); setDialogOpen(true) }
    function openDelete(f) { setCurrent(f); setDeleteOpen(true) }

    async function handleSave() {
        if (!libelle.trim()) return
        if (current) {
            const res = await doFetch(`/comptes/${eglise.number}/${current._id}`, {
                method: 'PATCH',
                body: JSON.stringify({ libelle, num_compte: numCompte || current.num_compte })
            })
            if (res.ok) { const updated = await res.json(); setFournisseurs(old => old.map(f => f._id === updated._id ? updated : f)) }
        } else {
            const res = await doFetch('/comptes/import-fournisseurs', {
                method: 'POST',
                body: JSON.stringify({ eglise: eglise.number, exercice, items: [{ libelle, num_compte: numCompte }] })
            })
            if (res.ok) { const created = await res.json(); setFournisseurs(old => [...old, ...created]) }
        }
        setDialogOpen(false)
    }

    async function handleDelete() {
        if (!current) return
        const res = await doFetch(`/comptes/${current.num}`, { method: 'DELETE' })
        if (res.ok) setFournisseurs(old => old.filter(f => f._id !== current._id))
        setDeleteOpen(false)
    }

    function handleCSV(e) {
        const file = e.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = async (ev) => {
            const lines = ev.target.result.split('\n').filter(l => l.trim())
            const items = lines.map(line => {
                const parts = line.split(';')
                return { num_compte: parts[0].trim(), libelle: parts.slice(1).join(';').trim() }
            }).filter(i => i.libelle)
            if (!items.length) return alert('Aucune ligne valide (format: num_compte;libellé)')
            const res = await doFetch('/comptes/import-fournisseurs', {
                method: 'POST',
                body: JSON.stringify({ eglise: eglise.number, exercice, items })
            })
            if (res.ok) { const created = await res.json(); setFournisseurs(old => [...old, ...created]) }
            else alert("Erreur lors de l'import CSV")
        }
        reader.readAsText(file)
        e.target.value = ''
    }

    return (
        <Box>
            <Header/>
            <Box sx={{ padding: 2 }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
                    <Typography variant='h6'>Comptes fournisseurs — {exercice}</Typography>
                    {organisation?.type === 'fédération' && (
                        <TextField select size='small' label='église' sx={{ width: 280 }} value={egliseId}
                            onChange={e => { setEgliseId(e.target.value); setFournisseurs([]) }}>
                            {eglises.sort((a, b) => a.nom.toLowerCase() < b.nom.toLowerCase() ? -1 : 1)
                                .map(eg => <MenuItem key={eg._id} value={eg._id}>{`${eg.nom} - ${eg.commune.toUpperCase()}`}</MenuItem>)}
                        </TextField>
                    )}
                </Stack>

                <Stack direction='row' spacing={2} sx={{ mb: 2 }} alignItems='center'>
                    <Button variant='contained' startIcon={<AddIcon/>} onClick={openCreate} disabled={!eglise}>Nouveau</Button>
                    <Button variant='outlined' startIcon={<UploadIcon/>} onClick={() => csvRef.current.click()} disabled={!eglise}>Import CSV</Button>
                    <Button variant='outlined' startIcon={<BarChartIcon/>} onClick={() => setGraphOpen(true)} disabled={fournisseurs.length === 0}>Graph</Button>
                    <input ref={csvRef} type='file' accept='.csv,.txt' style={{ display: 'none' }} onChange={handleCSV}/>
                </Stack>

                <Table size='small' sx={{ mb: 4 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>N° compte</TableCell>
                            <TableCell>Libellé</TableCell>
                            <TableCell align='right'>Solde (€)</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fournisseurs.map(f => (
                            <TableRow key={f._id}>
                                <TableCell>{f.num_compte}</TableCell>
                                <TableCell>{f.libelle}</TableCell>
                                <TableCell align='right' sx={{ color: (f.solde || 0) < 0 ? 'error.main' : 'text.primary' }}>
                                    {(f.solde || 0).toFixed(2)}
                                </TableCell>
                                <TableCell align='right'>
                                    <IconButton size='small' onClick={() => openEdit(f)}><EditIcon fontSize='small'/></IconButton>
                                    <IconButton size='small' color='error' onClick={() => openDelete(f)}><DeleteIcon fontSize='small'/></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && fournisseurs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align='center'>
                                    <Typography color='text.secondary'>
                                        {eglise ? 'Aucun compte fournisseur pour cet exercice' : 'Sélectionne une église'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='sm'>
                <DialogTitle>{current ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField fullWidth label='Libellé' value={libelle} onChange={e => setLibelle(e.target.value)} required autoFocus/>
                        <TextField fullWidth label='N° de compte comptable' value={numCompte} onChange={e => setNumCompte(e.target.value)} helperText='Optionnel — si vide, le numéro interne sera utilisé'/>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
                    <Button variant='contained' onClick={handleSave} disabled={!libelle.trim()}>Enregistrer</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={graphOpen} onClose={() => setGraphOpen(false)} fullWidth maxWidth='md'>
                <Graph
                    data={fournisseurs.map(f => ({ label: f.libelle, value: Math.abs(f.solde || 0) }))}
                    tt_label='Total dépensé'
                    line_data={{ dates: [], data: [] }}
                />
            </Dialog>

            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Supprimer</DialogTitle>
                <DialogContent>
                    <Typography>Supprimer le compte fournisseur <strong>{current?.libelle}</strong> ?</Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>Cette action est irréversible.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Annuler</Button>
                    <Button variant='contained' color='error' onClick={handleDelete}>Supprimer</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
